/* global URL_API_REST */
/**
 * PRD-037 — Cliente del backend del partner (panel-comercio).
 *
 * Usa una instancia axios DEDICADA sin el interceptor de auth de axiosInstance: las rutas
 * `/v1/partner/*` son Auth NONE en API Gateway y un header `Authorization` las rompe (API GW
 * lo intercepta como SigV4). El token de partner viaja en el header `partner-token`.
 * Las rutas `/v1/claim/*` son públicas (sin token).
 */
import axios from 'axios';
import type { UserType } from '@interfaces/general';

const partnerApi = axios.create({
  baseURL: URL_API_REST,
  timeout: 30 * 1000,
});

export interface PartnerInfo {
  id: string;
  usuario: string;
  nombre: string;
  modulos: string[];
}

export interface PartnerSession {
  token: string;
  partner: PartnerInfo;
}

export interface PartnerCommerce {
  id_suscripcion: string;
  id_comercio: string | null;
  partner_id: string;
  claimed_at: string | null;
  tipo_plan: string | null;
  id_cliente: string | null;
  estado: 'draft' | 'reclamado';
  /**
   * true cuando el dueño ya creó su cuenta (claimed_at) pero un intento de pago con tarjeta
   * sigue sin confirmarse (Stripe deja la suscripción en 'incomplete' hasta que el 3DS/
   * confirmPayment se resuelve). `estado === 'reclamado'` por sí solo NO prueba que el
   * funnel de pre-registro terminó — solo que la identidad quedó creada.
   */
  plan_pending: boolean;
  /** Usuario (login) del root creado en el claim. Solo se hidrata para comercios reclamados. */
  owner_usuario: string | null;
  negocio_nombre: string | null;
  owner_nombre: string | null;
  owner_correo: string | null;
  owner_telefono: string | null;
  created_at: string | null;
}

export interface DraftResult {
  draft: boolean;
  id_comercio: string;
  id_suscripcion: string;
  id_cliente: string;
  id_usuario_draft: string;
  partner_id: string;
  claimed_at: string | null;
  id_path: string;
}

export interface DraftSessionResult {
  jwt: string;
  id_comercio: string;
  id_usuario: string;
}

export interface ClaimLinkResult {
  token: string;
  claim_url: string;
  expires_at: number;
  ttl_seconds: number;
}

export interface ClaimOpenResult {
  negocio: {
    id_comercio: string;
    nombre?: string;
    direccion?: string;
    nombre_contacto?: string;
    telefono_contacto?: string;
    correo_contacto?: string;
  };
  menu_preview: { categorias: Array<{ id: string; nombre: string }> };
  otp_sent: boolean;
  phone: string | null;
  // En ambientes de prueba el backend acepta el OTP fijo 000000 y lo señala con esta bandera.
  // En producción llega false/ausente. El FE NO hardcodea el código por ambiente: lo deriva de aquí.
  otp_test_mode?: boolean;
}

// Payload listo para AuthContext.login() (sesión plena del comercio reclamado).
export interface CommerceSession {
  id_commerce: string;
  id_user: string;
  id_path: string;
  commerce_name: string;
  commerce_logo: string;
  user_name: string;
  token: string;
  user: { email: string; username: string; name: string; status: string };
  onboarding_finish_date: string | null;
  user_type: UserType;
  is_root: boolean;
  // Multisucursal: modulos/permisos del usuario (mismo shape que `AuthContext.login()` persiste como
  // `user_modules`). Opcional: si el backend del claim no lo incluye en la sesion, `persistSession`
  // simplemente no lo escribe (no-op). El dueno que reclama es root (is_root=true) y para root
  // `hasUserModule` se saltea, pero se persiste por simetria con el login normal y para no perder
  // permisos si alguna vez el claim produjera una sesion no-root.
  user_modules?: Array<{ id: string; permisos: string[] }>;
}

export interface ClaimCompleteResult {
  jwt: string;
  id_comercio: string;
  id_usuario: string;
  claimed_at: string | null;
  session: CommerceSession;
}

export interface ClaimResendResult {
  otp_sent: boolean;
  phone: string | null;
}

export interface ClaimVerifyOtpResult {
  valid: boolean;
}

export interface UpdateContactResult {
  id_comercio: string;
  contact: { nombre: string | null; email: string | null; telefono: string | null };
  updated: boolean;
}

const withToken = (partnerToken: string) => ({ headers: { 'partner-token': partnerToken } });

export const partnerLogin = async (usuario: string, password: string): Promise<PartnerSession> => {
  const { data } = await partnerApi.post<PartnerSession>('/v1/partner/login', { usuario, password });
  return data;
};

export const createDraft = async (
  partnerToken: string,
  body: {
    negocio: { nombre: string; direccion?: string; giro?: string; id_path?: string; lat?: number; lng?: number };
    owner: { nombre: string; email: string; whatsapp?: string; telefono?: string };
    monthly_orders?: string;
    product_interest?: string[];
  },
): Promise<DraftResult> => {
  const { data } = await partnerApi.post<DraftResult>('/v1/partner/commerces', body, withToken(partnerToken));
  return data;
};

export const listPartnerCommerces = async (partnerToken: string): Promise<PartnerCommerce[]> => {
  const { data } = await partnerApi.get<{ commerces: PartnerCommerce[] }>(
    '/v1/partner/commerces',
    withToken(partnerToken),
  );
  return data.commerces;
};

export const createDraftSession = async (partnerToken: string, idComercio: string): Promise<DraftSessionResult> => {
  const { data } = await partnerApi.post<DraftSessionResult>(
    '/v1/partner/draft-session',
    { id_comercio: idComercio },
    withToken(partnerToken),
  );
  return data;
};

export const createClaimLink = async (partnerToken: string, idComercio: string): Promise<ClaimLinkResult> => {
  const { data } = await partnerApi.post<ClaimLinkResult>(
    `/v1/partner/commerces/${idComercio}/claim-link`,
    {},
    withToken(partnerToken),
  );
  return data;
};

// Corrige el contacto del dueno de un draft. CRITICO: el telefono es el destino del OTP del claim;
// si quedo mal, el dueno nunca recibe el codigo. Solo aplica los campos provistos.
export const updateCommerceContact = async (
  partnerToken: string,
  idComercio: string,
  contact: { nombre?: string; email?: string; telefono?: string },
): Promise<UpdateContactResult> => {
  const { data } = await partnerApi.patch<UpdateContactResult>(
    `/v1/partner/commerces/${idComercio}/contact`,
    contact,
    withToken(partnerToken),
  );
  return data;
};

export const claimOpen = async (token: string): Promise<ClaimOpenResult> => {
  const { data } = await partnerApi.post<ClaimOpenResult>('/v1/claim/open', { token });
  return data;
};

// Reenvia el OTP del claim. Reusa el mismo mecanismo que el registro normal (el send reusa el
// codigo activo si existe). El backend deriva el telefono del comercio server-side.
export const claimResendOtp = async (token: string): Promise<ClaimResendResult> => {
  const { data } = await partnerApi.post<ClaimResendResult>('/v1/claim/resend-otp', { token });
  return data;
};

// Valida el OTP en modo PEEK: NO consume el token ni quema el código. Sirve para dar feedback en
// vivo mientras el dueño teclea (igual que el registro normal). 200 → {valid:true}; 400
// {error:'OTP_INVALID'} se propaga como excepción de axios. `claimComplete` sigue siendo el único
// que quema el código.
export const claimVerifyOtp = async (token: string, otpCode: string): Promise<ClaimVerifyOtpResult> => {
  const { data } = await partnerApi.post<ClaimVerifyOtpResult>('/v1/claim/verify-otp', {
    token,
    otp_code: otpCode,
  });
  return data;
};

export const claimComplete = async (body: {
  token: string;
  phone: string;
  otp_code: string;
  usuario: string;
  password: string;
  nombre?: string;
  correo?: string;
  acepta_terminos?: boolean;
}): Promise<ClaimCompleteResult> => {
  const { data } = await partnerApi.post<ClaimCompleteResult>('/v1/claim/complete', body);
  return data;
};
